import { boolean, object, string } from 'yup';
import type { CountryCode } from 'libphonenumber-js';

import { US } from '../../constants/country-codes';
import { buildMessageCallback } from '../../validation/utilities';
import '../../validation/custom-validators/phone-number-validator';
import { messages } from './messages';

export const FIRST_NAME_FIELD = 'firstName';
export const LAST_NAME_FIELD = 'lastName';
export const COMPANY_NAME_FIELD = 'companyName';
export const EMAIL_FIELD = 'email';
export const PHONE_NUMBER_FIELD = 'phoneNumber';

export const BILLING_FULL_NAME_FIELD = 'billingFullName';
export const BILLING_COMPANY_NAME_FIELD = 'billingCompanyName';
export const BILLING_ADDRESS_FIELD = 'billingAddress';
export const BILLING_PHONE_NUMBER_FIELD = 'billingPhoneNumber';

export const SHIPPING_ADDRESS_FIELD = 'shippingAddress';

export const PAYMENT_METHOD_ID_FIELD = 'paymentMethodId';
export const AUTO_PAY_ACCEPTED_FIELD = 'autoPayAccepted';
export const TERMS_ACCEPTED_FIELD = 'termsAccepted';

// Address validation schema helper
// Note: Addresses are always required when shown in the form
const buildAddressSchema = (isRequired: boolean = true) => {
  if (!isRequired) {
    return object({
      line1: string().optional(),
      line2: string().optional(),
      line3: string().optional(),
      city: string().optional(),
      state: string().optional(),
      postalCode: string().optional(),
      countryCode: string().optional(),
    });
  }

  return object({
    line1: string().required(buildMessageCallback(messages.requiredFieldError)),
    line2: string().optional(),
    line3: string().optional(),
    city: string().required(buildMessageCallback(messages.requiredFieldError)),
    state: string().required(buildMessageCallback(messages.requiredFieldError)),
    postalCode: string().required(buildMessageCallback(messages.requiredFieldError)),
    countryCode: string().optional(),
  });
};

export const buildWwexFormSchema = (countryCode: string = US, isBillingAddressRequired: boolean = true) =>
  object({
    // User Information
    [FIRST_NAME_FIELD]: string().required(buildMessageCallback(messages.requiredFieldError)),
    [LAST_NAME_FIELD]: string().required(buildMessageCallback(messages.requiredFieldError)),
    [COMPANY_NAME_FIELD]: string().required(buildMessageCallback(messages.requiredFieldError)),
    [EMAIL_FIELD]: string()
      .required(buildMessageCallback(messages.requiredFieldError))
      .email(buildMessageCallback(messages.invalidEmailError)),
    [PHONE_NUMBER_FIELD]: string()
      .required(buildMessageCallback(messages.requiredFieldError))
      .isValidPhoneNumberForCountry(countryCode as CountryCode),

    // Billing Address (required when existing payment method selected)
    [BILLING_FULL_NAME_FIELD]: isBillingAddressRequired
      ? string().required(buildMessageCallback(messages.requiredFieldError))
      : string().optional(),
    [BILLING_COMPANY_NAME_FIELD]: string().optional(),
    [BILLING_ADDRESS_FIELD]: buildAddressSchema(isBillingAddressRequired),

    // Shipping Address (always derived from billing — no independent validation needed)
    [SHIPPING_ADDRESS_FIELD]: buildAddressSchema(false),

    // Payment Method
    [PAYMENT_METHOD_ID_FIELD]: string().required(buildMessageCallback(messages.auctanePayAccountRequired)),

    // Authorizations
    [AUTO_PAY_ACCEPTED_FIELD]: boolean()
      .required(buildMessageCallback(messages.autoPayRequiredError))
      .oneOf([true], buildMessageCallback(messages.autoPayRequiredError)),
    [TERMS_ACCEPTED_FIELD]: boolean()
      .required(buildMessageCallback(messages.termsRequiredError))
      .oneOf([true], buildMessageCallback(messages.termsRequiredError)),
  });
